import { useEffect, useRef, useState } from "react";
import { placedPdfSchema, type DesignNode, type PlacedPdf } from "@proof/core";
import { Button, Control, Toggle } from "./components";
import { useAuthoringServices } from "./services";

export function PdfArtwork({
  node,
  onApply,
  onRemove,
}: {
  node: DesignNode;
  onApply: (artwork: PlacedPdf, keepPlacement: boolean) => void;
  onRemove: () => void;
}) {
  const { importPdfArtwork } = useAuthoringServices();
  const [file, setFile] = useState<File>();
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<PlacedPdf>();
  const [keepPlacement, setKeepPlacement] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef<AbortController | undefined>(undefined);
  const cancel = () => {
    pending.current?.abort();
    pending.current = undefined;
    setBusy(false);
  };
  useEffect(() => {
    cancel();
    setDraft(undefined);
    setError("");
    return () => {
      pending.current?.abort();
      pending.current = undefined;
    };
  }, [node.id, node.content, node.placedPdf?.sha256, importPdfArtwork]);
  const validPage = Number.isInteger(page) && page >= 1 && page <= 100;
  const prepare = async () => {
    if (!file || !validPage || !importPdfArtwork) return;
    cancel();
    const controller = new AbortController();
    pending.current = controller;
    setBusy(true);
    setError("");
    setDraft(undefined);
    try {
      const result = placedPdfSchema.parse(
        await importPdfArtwork(file, page, controller.signal),
      );
      if (pending.current !== controller || controller.signal.aborted) return;
      setDraft(result);
    } catch (e) {
      if (pending.current === controller && !controller.signal.aborted)
        setError(
          e instanceof Error ? e.message : "PDF artwork could not be imported",
        );
    } finally {
      if (pending.current === controller) {
        pending.current = undefined;
        setBusy(false);
      }
    }
  };
  return (
    <details className="pdf-artwork">
      <summary>PDF artwork</summary>
      <p className="inspector-note">
        Replace the image with a PDF page. Keep the frame and its variable
        binding; print PDF profiles retain vector artwork and original paint
        colors. Preview and raster output use managed pixels.
      </p>
      {node.placedPdf && (
        <p>
          Retained PDF: {node.placedPdf.width} × {node.placedPdf.height} pt
        </p>
      )}
      <Control label="Replacement PDF">
        <input
          type="file"
          accept="application/pdf,.pdf"
          disabled={!importPdfArtwork}
          onChange={(e) => {
            cancel();
            setDraft(undefined);
            setError("");
            setFile(e.target.files?.[0]);
          }}
        />
      </Control>
      <Control label="PDF page">
        <input
          type="number"
          min={1}
          max={100}
          step={1}
          value={page}
          onChange={(e) => {
            cancel();
            setDraft(undefined);
            setError("");
            setPage(Number(e.target.value));
          }}
        />
      </Control>
      {!validPage && (
        <p role="alert">Choose a whole page number from 1 to 100.</p>
      )}
      <Toggle
        label="Keep current image placement"
        value={keepPlacement}
        onChange={setKeepPlacement}
      />
      <Button
        disabled={!importPdfArtwork || !file || !validPage || busy}
        onClick={() => void prepare()}
      >
        {busy ? "Preparing PDF artwork…" : "Prepare PDF artwork"}
      </Button>
      {busy && <Button onClick={cancel}>Cancel PDF import</Button>}
      {error && <p role="alert">{error}</p>}
      {draft && (
        <>
          <p>
            Ready: {draft.width} × {draft.height} pt. Apply updates the image
            and its variable’s default. Existing part overrides remain.
          </p>
          <Button
            onClick={() => {
              try {
                onApply(draft, keepPlacement);
              } catch (e) {
                setError(
                  e instanceof Error
                    ? e.message
                    : "Artwork could not be applied",
                );
              }
            }}
          >
            Apply PDF artwork
          </Button>
          <Button onClick={() => setDraft(undefined)}>
            Discard prepared PDF
          </Button>
        </>
      )}
      {node.placedPdf && (
        <Button onClick={onRemove}>Use preview pixels only</Button>
      )}
    </details>
  );
}
